import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ManageSearchRounded from '@mui/icons-material/ManageSearchRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import type { ResourceListItemDto } from '../../../../shared/types/access';
import { color, font, glass, monoLabel, radius } from '../../design/tokens';
import { featureFor } from '../../features';
import { resourcePath } from '../../features/registry';
import { useResourceSearch, useResources } from '../../services/platform';
import { timeAgo } from '../../utils/format';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const SEARCH_SHORTCUT_LABEL = isMac ? '⌘K' : 'Ctrl K';

/** Ctrl/⌘ K anywhere, or "/" when not typing, opens search. */
export function useSearchShortcut(open: () => void) {
    useEffect(() => {
        const onKey = (e: globalThis.KeyboardEvent) => {
            const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName));
            if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                open();
            } else if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
                e.preventDefault();
                open();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);
}

/** Highlight the part of the title that matches the query. */
function Highlighted({ text, query }: { text: string; query: string }) {
    const q = query.trim();
    const at = q ? text.toLocaleLowerCase().indexOf(q.toLocaleLowerCase()) : -1;
    if (at < 0) return <>{text}</>;
    return (
        <>
            {text.slice(0, at)}
            <Box component="mark" sx={{ backgroundColor: 'transparent', color: color.spark, fontWeight: 600 }}>{text.slice(at, at + q.length)}</Box>
            {text.slice(at + q.length)}
        </>
    );
}

function ResultRow({ item, query, active, onPick, onHover, id }: {
    item: ResourceListItemDto; query: string; active: boolean; onPick: () => void; onHover: () => void; id: string;
}) {
    const manifest = featureFor(item.type);
    const meta = item.role === 'admin'
        ? (item.archived ? `Archived · ${timeAgo(item.updatedAt)}` : `Updated ${timeAgo(item.updatedAt)}`)
        : `${item.ownerName} · shared with you`;
    return (
        <ButtonBase
            id={id}
            role="option"
            aria-selected={active}
            onClick={onPick}
            onMouseMove={onHover}
            sx={{
                width: '100%', justifyContent: 'flex-start', textAlign: 'left', gap: 1.5, px: 1.5, py: 1.25,
                borderRadius: `${radius.sm}px`, color: 'inherit',
                backgroundColor: active ? color.signalSoft : 'transparent',
                opacity: item.archived ? 0.72 : 1,
            }}
        >
            <Box sx={{
                width: 34, height: 34, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center',
                color: manifest.accent, backgroundColor: `${manifest.accent}1f`, '& svg': { fontSize: 18 },
            }}>
                <manifest.Icon />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontFamily: font.display, fontWeight: 500 }}>
                    <Highlighted text={item.title} query={query} />
                </Typography>
                <Typography noWrap variant="body2" sx={{ color: color.textFaint, fontSize: 12.5 }}>
                    {manifest.label} · {meta}
                </Typography>
            </Box>
            {item.restricted && <LockOutlined sx={{ fontSize: 16, color: color.textMuted }} />}
        </ButtonBase>
    );
}

/**
 * Find a board, retro or goal space by name: mine (archived too) and ones shared with me.
 * With nothing typed it shows recent spaces. With content search switched on, Enter while typing opens the
 * full results page (titles and contents) and arrow down picks a single space; otherwise Enter opens the
 * highlighted space. Esc closes.
 */
export function SearchDialog({ open, onClose, contentSearch = false }: {
    open: boolean;
    onClose: () => void;
    /** Content search is switched on (feature gate): Enter goes to the full results page. */
    contentSearch?: boolean;
}) {
    const navigate = useNavigate();
    const phone = useMediaQuery('(max-width:600px)');
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const search = useResourceSearch(query);
    const owned = useResources('owned', open && !query.trim());
    const shared = useResources('shared', open && !query.trim());

    const typing = !!query.trim();
    const results = useMemo<ResourceListItemDto[]>(() => {
        if (typing) return search.data ?? [];
        // Recent: mine and shared, most recently active first
        const recent = [...(owned.data ?? []), ...(shared.data ?? [])];
        return recent
            .sort((a, b) => (b.lastVisitedAt ?? b.updatedAt).localeCompare(a.lastVisitedAt ?? a.updatedAt))
            .slice(0, 8);
    }, [typing, search.data, owned.data, shared.data]);

    useEffect(() => { setActive(0); }, [query, open]);
    useEffect(() => { if (!open) setQuery(''); }, [open]);

    // With content search on, row 0 while typing is "Search everything", so Enter goes to the full
    // results page; arrow down to open a single space instead. Off: Enter opens the highlighted space.
    const everything = typing && contentSearch;
    const offset = everything ? 1 : 0;
    const rowCount = results.length + offset;

    const pick = (item: ResourceListItemDto | undefined) => {
        if (!item) return;
        onClose();
        navigate(resourcePath(featureFor(item.type), item.shareId));
    };
    const searchEverything = () => {
        onClose();
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    };

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive(a => Math.min(rowCount - 1, a + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(a => Math.max(0, a - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (everything && active === 0) searchEverything();
            else pick(results[active - offset]);
        }
    };

    // Keep the highlighted row in view while arrowing through a long list
    useEffect(() => {
        document.getElementById(`cs-search-${active}`)?.scrollIntoView?.({ block: 'nearest' });
    }, [active]);

    const loading = typing ? search.isFetching : owned.isLoading || shared.isLoading;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            fullScreen={phone}
            TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
            PaperProps={{
                sx: {
                    ...glass,
                    backgroundColor: 'rgba(14, 12, 38, 0.94)',
                    alignSelf: phone ? 'stretch' : 'flex-start',
                    mt: phone ? 0 : '12vh',
                    maxHeight: phone ? '100%' : '70vh',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, position: 'sticky', top: 0 }}>
                {phone ? (
                    <IconButton aria-label="Close search" onClick={onClose} edge="start"><ArrowBackRounded /></IconButton>
                ) : (
                    <SearchRounded sx={{ color: color.textMuted, mx: 0.5 }} />
                )}
                <InputBase
                    inputRef={inputRef}
                    autoFocus
                    fullWidth
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Search boards, retros and goal spaces"
                    inputProps={{
                        'aria-label': 'Search your spaces',
                        role: 'combobox',
                        'aria-expanded': true,
                        'aria-controls': 'cs-search-results',
                        'aria-activedescendant': rowCount ? `cs-search-${active}` : undefined,
                        maxLength: 100,
                    }}
                    sx={{ fontSize: 17, py: 0.75 }}
                />
                {loading && <CircularProgress size={16} sx={{ color: color.textMuted, mr: 0.5 }} />}
                {!phone && (
                    <Typography component="kbd" sx={{ fontFamily: font.mono, fontSize: 11, color: color.textFaint, px: 0.75, py: 0.25, borderRadius: '3px', backgroundColor: 'rgba(140,170,255,0.08)' }}>
                        Esc
                    </Typography>
                )}
            </Box>
            <Box sx={{ height: '1px', backgroundColor: color.line }} />
            <Box id="cs-search-results" role="listbox" aria-label="Search results" sx={{ overflowY: 'auto', p: 1 }}>
                {!typing && results.length > 0 && (
                    <Typography sx={{ px: 1.5, pt: 0.5, pb: 1, ...monoLabel(11), color: color.textFaint }}>
                        Recent
                    </Typography>
                )}
                {everything && (
                    <ButtonBase
                        id="cs-search-0"
                        role="option"
                        aria-selected={active === 0}
                        onClick={searchEverything}
                        onMouseMove={() => setActive(0)}
                        sx={{
                            width: '100%', justifyContent: 'flex-start', textAlign: 'left', gap: 1.5, px: 1.5, py: 1.25, mb: results.length ? 0.5 : 0,
                            borderRadius: `${radius.sm}px`, color: 'inherit', backgroundColor: active === 0 ? color.signalSoft : 'transparent',
                        }}
                    >
                        <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', color: color.signal, backgroundColor: color.signalSoft }}>
                            <ManageSearchRounded sx={{ fontSize: 20 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography noWrap sx={{ fontFamily: font.display, fontWeight: 500 }}>
                                Search everything for “{query.trim()}”
                            </Typography>
                            <Typography noWrap variant="body2" sx={{ color: color.textFaint, fontSize: 12.5 }}>
                                Titles, stickies, cards, goals, comments
                            </Typography>
                        </Box>
                        {!phone && (
                            <Typography component="kbd" sx={{ fontFamily: font.mono, fontSize: 11, color: color.textFaint, px: 0.75, py: 0.25, borderRadius: '3px', backgroundColor: 'rgba(140,170,255,0.08)' }}>
                                Enter
                            </Typography>
                        )}
                    </ButtonBase>
                )}
                {results.map((item, i) => (
                    <ResultRow
                        key={`${item.type}:${item.shareId}`}
                        id={`cs-search-${i + offset}`}
                        item={item}
                        query={query}
                        active={i + offset === active}
                        onPick={() => pick(item)}
                        onHover={() => setActive(i + offset)}
                    />
                ))}
                {typing && !search.isFetching && results.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2, textAlign: 'center' }}>
                        {contentSearch ? <>No space is called “{query.trim()}”. Enter searches inside.</> : <>Nothing called “{query.trim()}”.</>}
                    </Typography>
                )}
                {!typing && !loading && results.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 4, textAlign: 'center' }}>
                        Type to find a space.
                    </Typography>
                )}
            </Box>
        </Dialog>
    );
}
